import React, { useEffect } from 'react';
import { css } from 'emotion';
import { Modal } from '@spotify-internal/anchor-ds';
import { useT } from 'client/i18n/hooks/useT';
import {
  Banner,
  ButtonPrimary,
  ButtonSecondary,
  TextLink,
} from '@spotify-internal/encore-web';
import { RetinaPicture } from 'components/RetinaPicture';
import { paths } from 'client/shared/Paths';
import { events } from './events';

function EnableRSSFeedModal({
  onClickClose,
  onClickEnable,
}: {
  onClickClose: () => void;
  onClickEnable: () => void;
}) {
  const t = useT();
  // analytics
  useEffect(() => {
    events.enableRssModalScreenViewed();
  }, []);

  return (
    <Modal
      isShowing
      isShowingCloseButton
      aria-labelledby="enable-rss-feed-modal-title"
      onClose={() => {
        events.enableRssModalExitButtonClicked();
        onClickClose();
      }}
    >
      <Modal.Image as="div">
        <RetinaPicture
          imagePath="/distribution/rss-icon"
          alt={t(
            'PreDistributionModalScreen.BinaryDistributionPrompt.ImgAlt',
            "Minimalized display of Spotify's mobile user interface",
            'Enable RSS feed confirmation modal image description'
          )}
          fallbackExtension="png"
        />
      </Modal.Image>
      <Modal.Title id="enable-rss-feed-modal-title">
        {t(
          'PreDistributionModalScreen.BinaryDistributionPrompt.EnableRSSFeedModal.Title',
          'Enable your RSS feed?',
          'Title of the enable RSS feed modal to all podcast to appear in other podcast apps'
        )}
      </Modal.Title>
      <Modal.Subtitle>
        {t(
          'PreDistributionModalScreen.BinaryDistributionPrompt.EnableRSSFeedModal.Subtitle',
          'Your RSS feed allows your podcast to appear in other podcast apps, in some cases automatically.',
          'Subtitle of the enable RSS feed modal to all podcast to appear in other podcast apps'
        )}
      </Modal.Subtitle>
      <Modal.Body>
        <Banner
          className={css`
            margin-top: 16px;
            border-radius: 8px;
          `}
        >
          {t(
            'PreDistributionModalScreen.BinaryDistributionPrompt.EnableRSSFeedModal.Message',
            'This feed is public and contains your email address. {helpPageLink} to learn more.',
            'Enable RSS feed confirmation modal message',
            {
              helpPageLink: (
                <TextLink
                  key="help-link"
                  rel="ugc noopener noreferrer"
                  target="_blank"
                  href={paths.getHelpPage('whatIsAnRSSfeed')}
                >
                  {t('Link.ClickHere', 'Click here', 'Click here link text')}
                </TextLink>
              ),
            }
          )}
        </Banner>
      </Modal.Body>
      <Modal.Footer>
        <ButtonSecondary
          onClick={() => {
            events.enableRssModalNotYetButtonClicked();
            onClickClose();
          }}
        >
          {t(
            'PreDistributionModalScreen.BinaryDistributionPrompt.EnableRSSFeedModal.DontEnableRSSButtonLabel',
            'Not yet',
            "Don't enable RSS feed yet button text"
          )}
        </ButtonSecondary>
        <ButtonPrimary
          onClick={() => {
            events.enableRssModalEnableFeedButtonClicked();
            onClickEnable();
          }}
        >
          {t(
            'PreDistributionModalScreen.BinaryDistributionPrompt.EnableRSSFeedModal.EnableRSSButtonLabel',
            'Enable',
            'Enable RSS feed button text'
          )}
        </ButtonPrimary>
      </Modal.Footer>
    </Modal>
  );
}

export { EnableRSSFeedModal };
